import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Fuel, Plus, Loader2, X, AlertTriangle, Filter, Camera, Edit3, Trash2, FileText } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import dict from "../i18n/es-MX";
import { fmtDate } from "../lib/dateFormat";
import { useAuth } from "../contexts/AuthContext";
import { useForm } from "../components/forms/useForm";
import { Field, NumberInput, DateInput, SelectInput, TextInput, Textarea } from "../components/forms/Field";
import { required } from "../lib/validation";
import EmptyState from "../components/EmptyState";
import ErrorBoundary from "../components/ErrorBoundary";

const fmtMXN = (n) => n == null ? "—" : new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n);
const fmtNum = (n, d = 2) => n == null ? "—" : Number(n).toLocaleString("es-MX", { maximumFractionDigits: d });

const FUEL_TYPES = ["gasolina", "diesel", "premium"];
const PAYMENT_METHODS = ["efectivo", "transferencia", "vale", "tarjeta_combustible"];

const TYPE_BADGE = {
    gasolina: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    diesel: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    premium: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function firstOfMonthISO() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }

export default function Combustible() {
    const { user, bootstrapping } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (!bootstrapping && user && user.role === "operator") navigate("/campo", { replace: true });
    }, [user, bootstrapping, navigate]);

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [toast, setToast] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Filters
    const [filterAssetId, setFilterAssetId] = useState("");
    const [filterOperatorId, setFilterOperatorId] = useState("");
    const [filterFuelType, setFilterFuelType] = useState("");
    const [filterMethod, setFilterMethod] = useState("");
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");
    const [onlyNoJob, setOnlyNoJob] = useState(false);

    // Master data for filters + form dropdowns
    const [assets, setAssets] = useState([]);
    const [operators, setOperators] = useState([]);
    const [jobs, setJobs] = useState([]);

    // Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [a, o, j] = await Promise.all([
                    api.get("/assets"),
                    api.get("/operators"),
                    api.get("/jobs"),
                ]);
                setAssets((a.data || []).filter(x => !x.archived));
                setOperators((o.data || []).filter(x => !x.archived));
                setJobs((j.data || []).filter(x => x.status === "programado" || x.status === "en_progreso"));
            } catch { /* silent */ }
        })();
    }, []);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const params = {};
            if (filterAssetId) params.asset_id = filterAssetId;
            if (filterOperatorId) params.operator_id = filterOperatorId;
            if (filterFuelType) params.fuel_type = filterFuelType;
            if (filterMethod) params.payment_method = filterMethod;
            if (filterFrom) params.date_from = filterFrom;
            if (filterTo) params.date_to = filterTo;
            if (onlyNoJob) params.job_id = "none";
            const [lr, sr] = await Promise.all([
                api.get("/fuel-logs", { params }),
                api.get("/fuel-logs/summary", { params: { date_from: firstOfMonthISO(), date_to: todayISO() } }),
            ]);
            setRows(lr.data || []);
            setSummary(sr.data || null);
        } catch (e) {
            setErr(formatApiError(e) || dict.fuel.loadError);
        } finally {
            setLoading(false);
        }
    }, [filterAssetId, filterOperatorId, filterFuelType, filterMethod, filterFrom, filterTo, onlyNoJob]);
    useEffect(() => { load(); }, [load]);

    const clearFilters = () => {
        setFilterAssetId(""); setFilterOperatorId(""); setFilterFuelType("");
        setFilterMethod(""); setFilterFrom(""); setFilterTo(""); setOnlyNoJob(false);
    };
    const hasFilters = filterAssetId || filterOperatorId || filterFuelType || filterMethod || filterFrom || filterTo || onlyNoJob;

    const openNew = () => { setEditing(null); setModalOpen(true); };
    const openEdit = (row) => { setEditing(row); setModalOpen(true); };

    const doDelete = async (row) => {
        if (!window.confirm(dict.fuel.confirmDelete)) return;
        try {
            await api.delete(`/fuel-logs/${row.id}`);
            setToast({ msg: dict.fuel.deleteOk, type: "success" });
            load();
        } catch (e) {
            setToast({ msg: formatApiError(e), type: "error" });
        }
    };

    return (
        <div className="px-4 md:px-8 py-6 space-y-5" data-testid="combustible-page">
            <header className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
                        <Fuel size={22} className="text-brand-orange"/>
                        {dict.fuel.title}
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">{dict.fuel.subtitle}</p>
                </div>
                <button type="button" onClick={openNew} data-testid="fuel-new-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold text-sm uppercase tracking-wider">
                    <Plus size={16}/> {dict.fuel.newBtn}
                </button>
            </header>

            {/* KPI cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fuel-kpi">
                    <KpiCard label={dict.fuel.kpi.thisMonth} value={fmtMXN(summary.total_cost_mxn)}
                        sub={`${fmtNum(summary.total_liters, 0)} ${dict.fuel.kpi.litersSuffix}`} testid="kpi-month"/>
                    <KpiCard label={dict.fuel.kpi.avgPerLiter} value={fmtMXN(summary.avg_cost_per_liter)}
                        testid="kpi-avg"/>
                    <KpiCard label="Tipos" value={<TypeBreakdown types={summary.by_type}/>} small testid="kpi-types"/>
                    <KpiCard label="Métodos" value={<MethodBreakdown methods={summary.by_payment_method}/>} small testid="kpi-methods"/>
                </div>
            )}

            {/* Filters */}
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <div className="flex items-center justify-between mb-2">
                    <button type="button" onClick={() => setShowFilters(s => !s)} data-testid="fuel-filters-toggle"
                        className="text-xs uppercase tracking-wider text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
                        <Filter size={14}/> {showFilters ? "Ocultar filtros" : "Filtros"}
                        {hasFilters && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-slate-950 text-[10px] font-bold">•</span>}
                    </button>
                    {hasFilters && (
                        <button type="button" onClick={clearFilters} data-testid="fuel-clear-filters"
                            className="text-xs text-slate-400 hover:text-red-300 inline-flex items-center gap-1">
                            <X size={12}/> {dict.fuel.filters.clear}
                        </button>
                    )}
                </div>
                {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-800" data-testid="fuel-filters-panel">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.fuel.filters.asset}</div>
                            <select value={filterAssetId} onChange={e => setFilterAssetId(e.target.value)} data-testid="fuel-filter-asset"
                                className="w-full h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm">
                                <option value="">{dict.fuel.filters.allAssets}</option>
                                {assets.map(a => <option key={a.id} value={a.id}>{a.nickname}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.fuel.filters.operator}</div>
                            <select value={filterOperatorId} onChange={e => setFilterOperatorId(e.target.value)} data-testid="fuel-filter-operator"
                                className="w-full h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm">
                                <option value="">{dict.fuel.filters.allOperators}</option>
                                {operators.map(o => <option key={o.id} value={o.id}>{o.full_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.fuel.filters.dateRange}</div>
                            <div className="flex gap-1">
                                <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} data-testid="fuel-filter-from"
                                    className="flex-1 h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm text-slate-100"/>
                                <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} data-testid="fuel-filter-to"
                                    className="flex-1 h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm text-slate-100"/>
                            </div>
                        </div>
                        <div className="md:col-span-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.fuel.filters.fuelType}</div>
                            <div className="flex gap-2 flex-wrap">
                                {FUEL_TYPES.map(t => (
                                    <button key={t} type="button" data-testid={`fuel-filter-type-${t}`}
                                        onClick={() => setFilterFuelType(filterFuelType === t ? "" : t)}
                                        className={`px-3 h-8 rounded-full border text-xs ${filterFuelType===t ? "bg-brand-orange text-slate-950 border-brand-orange font-semibold" : "bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                        {dict.fuel.types[t]}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="md:col-span-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.fuel.filters.paymentMethod}</div>
                            <div className="flex gap-2 flex-wrap">
                                {PAYMENT_METHODS.map(m => (
                                    <button key={m} type="button" data-testid={`fuel-filter-method-${m}`}
                                        onClick={() => setFilterMethod(filterMethod === m ? "" : m)}
                                        className={`px-3 h-8 rounded-full border text-xs ${filterMethod===m ? "bg-brand-orange text-slate-950 border-brand-orange font-semibold" : "bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                        {dict.fuel.methods[m]}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="md:col-span-3">
                            <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                                <input type="checkbox" checked={onlyNoJob} onChange={e => setOnlyNoJob(e.target.checked)}
                                    data-testid="fuel-filter-nojob" className="accent-brand-orange"/>
                                {dict.fuel.filters.onlyNoJob}
                            </label>
                        </div>
                    </div>
                )}
            </section>

            {/* Table */}
            {loading ? (
                <div className="py-12 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>
            ) : err ? (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="fuel-error">{err}</div>
            ) : rows.length === 0 ? (
                <EmptyState icon={Fuel}
                    title="Aún no hay cargas de combustible"
                    description="Empieza registrando la primera carga. También puedes registrar cargas desde el operador en /campo."
                    ctaLabel="Registrar la primera carga"
                    onCta={openNew}
                    secondaryHint="También puedes registrar cargas desde /campo (operador)."
                    testid="fuel-empty"/>
            ) : (
                <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="fuel-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2">{dict.fuel.table.date}</th>
                                <th className="text-left px-3 py-2">{dict.fuel.table.asset}</th>
                                <th className="text-left px-3 py-2">{dict.fuel.table.operator}</th>
                                <th className="text-left px-3 py-2">{dict.fuel.table.job}</th>
                                <th className="text-left px-3 py-2">{dict.fuel.table.fuelType}</th>
                                <th className="text-right px-3 py-2">{dict.fuel.table.liters}</th>
                                <th className="text-right px-3 py-2">{dict.fuel.table.cost}</th>
                                <th className="text-right px-3 py-2">{dict.fuel.table.pricePerLiter}</th>
                                <th className="text-right px-3 py-2">{dict.fuel.table.kmPerLiter}</th>
                                <th className="text-left px-3 py-2">{dict.fuel.table.method}</th>
                                <th className="text-left px-3 py-2">{dict.fuel.table.voucher}</th>
                                <th className="text-center px-3 py-2">{dict.fuel.table.receipt}</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} data-testid={`fuel-row-${r.id}`}
                                    className={`border-t border-slate-800 ${r.anomaly_flag ? "bg-amber-500/10" : "hover:bg-slate-900/40"}`}
                                    title={r.anomaly_flag ? `${dict.fuel.anomalyTooltip}: ${r.anomaly_reason || ""}` : ""}>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-300">
                                        {r.anomaly_flag && <AlertTriangle size={12} className="inline text-amber-400 mr-1"/>}
                                        {fmtDate(r.date)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <Link to={`/activos/${r.asset_id}`} className="text-slate-200 hover:text-brand-orange">
                                            {r.asset_nickname || "—"}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2 text-slate-300">{r.operator_name || "—"}</td>
                                    <td className="px-3 py-2">
                                        {r.job_id ? (
                                            <Link to={`/trabajos/${r.job_id}`} className="inline-flex px-2 py-0.5 rounded font-mono text-[11px] bg-sky-500/15 text-sky-300 border border-sky-500/40 hover:bg-sky-500/25">
                                                {r.job_code}
                                            </Link>
                                        ) : (
                                            <span className="inline-flex px-2 py-0.5 rounded text-[11px] bg-slate-800 text-slate-500 border border-slate-700">{dict.fuel.table.noJob}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${TYPE_BADGE[r.fuel_type] || ""}`}>
                                            {dict.fuel.types[r.fuel_type] || r.fuel_type}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtNum(r.liters, 1)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtMXN(r.total_cost_mxn)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-400">{r.cost_per_liter != null ? fmtMXN(r.cost_per_liter) : "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-400">{r.km_per_liter != null ? fmtNum(r.km_per_liter, 1) : "—"}</td>
                                    <td className="px-3 py-2 text-slate-300 text-xs">{dict.fuel.methods[r.payment_method] || r.payment_method}</td>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-400">{r.voucher_number || "—"}</td>
                                    <td className="px-3 py-2 text-center">{r.photo_id ? <FileText size={14} className="inline text-emerald-400"/> : <span className="text-slate-600">—</span>}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <button type="button" onClick={() => openEdit(r)} data-testid={`fuel-edit-${r.id}`}
                                            className="text-slate-400 hover:text-brand-orange mr-2"><Edit3 size={14}/></button>
                                        {user?.role === "admin" && (
                                            <button type="button" onClick={() => doDelete(r)} data-testid={`fuel-delete-${r.id}`}
                                                className="text-slate-400 hover:text-red-400"><Trash2 size={14}/></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md text-sm shadow-lg z-50 border ${toast.type==="error"?"bg-red-500/20 border-red-500/40 text-red-200":"bg-slate-800 text-slate-100 border-slate-700"}`} data-testid="fuel-toast" onAnimationEnd={() => setToast(null)}>
                    {toast.msg}
                </div>
            )}

            {modalOpen && (
                <ErrorBoundary description={dict.fuel.form.crashMsg || "No se pudo cargar el formulario de combustible. Refresca la página."}>
                    <FuelLogModal
                        open={modalOpen}
                        editing={editing}
                        assets={assets}
                        operators={operators}
                        jobs={jobs}
                        onClose={() => { setModalOpen(false); setEditing(null); }}
                        onSaved={(msg) => { setModalOpen(false); setEditing(null); setToast({ msg, type: "success" }); load(); }}
                    />
                </ErrorBoundary>
            )}
        </div>
    );
}

function KpiCard({ label, value, sub, testid, small }) {
    return (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`${small?"text-xs":"text-lg"} font-mono font-bold text-slate-100 mt-1`}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}
function TypeBreakdown({ types }) {
    if (!types?.length) return <span className="text-slate-500 text-sm">—</span>;
    return (
        <div className="space-y-0.5">
            {types.map(t => (
                <div key={t.fuel_type} className="flex justify-between gap-2 text-[11px]">
                    <span className="text-slate-400">{dict.fuel.types[t.fuel_type]}</span>
                    <span className="font-mono text-slate-200">{fmtNum(t.liters, 0)}L</span>
                </div>
            ))}
        </div>
    );
}
function MethodBreakdown({ methods }) {
    if (!methods?.length) return <span className="text-slate-500 text-sm">—</span>;
    return (
        <div className="space-y-0.5">
            {methods.map(m => (
                <div key={m.method} className="flex justify-between gap-2 text-[11px]">
                    <span className="text-slate-400">{dict.fuel.methods[m.method]}</span>
                    <span className="font-mono text-slate-200">{fmtMXN(m.cost)}</span>
                </div>
            ))}
        </div>
    );
}

// ============================================================
// Modal (Nueva / Editar carga)
// ============================================================
function FuelLogModal({ open, editing, assets, operators, jobs, onClose, onSaved }) {
    // Defensive defaults: parent could still be loading these arrays.
    const _assets = Array.isArray(assets) ? assets : [];
    const _operators = Array.isArray(operators) ? operators : [];
    const _jobs = Array.isArray(jobs) ? jobs : [];

    const form = useForm({
        initial: {
            asset_id: editing?.asset_id || "",
            date: editing?.date?.slice(0, 10) || todayISO(),
            fuel_type: editing?.fuel_type || "diesel",
            liters: editing?.liters ?? "",
            total_cost_mxn: editing?.total_cost_mxn ?? "",
            payment_method: editing?.payment_method || "efectivo",
            voucher_number: editing?.voucher_number || "",
            operator_id: editing?.operator_id || "",
            job_id: editing?.job_id || "",
            odometer_in_km: editing?.odometer_in_km ?? "",
            odometer_out_km: editing?.odometer_out_km ?? "",
            hour_meter_in: editing?.hour_meter_in ?? "",
            hour_meter_out: editing?.hour_meter_out ?? "",
            notes: editing?.notes || "",
        },
        validators: {
            asset_id: required,
            date: required,
            liters: (v) => !v || parseFloat(v) <= 0 ? "Litros debe ser mayor a 0" : "",
            total_cost_mxn: (v) => !v || parseFloat(v) <= 0 ? "Costo debe ser mayor a 0" : "",
        },
    });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const v = form.values;
    const price = (parseFloat(v.liters) > 0 && parseFloat(v.total_cost_mxn) > 0)
        ? parseFloat(v.total_cost_mxn) / parseFloat(v.liters) : null;

    // Jobs filtered by selected asset
    const availableJobs = useMemo(() => {
        if (!v.asset_id) return [];
        return _jobs.filter(j => (j.asset_ids || []).includes(v.asset_id));
    }, [v.asset_id, _jobs]);

    const submit = (e) => {
        e.preventDefault();
        form.handleSubmit(async (vals) => {
            setBusy(true); setErr("");
            try {
                if (editing) {
                    const body = {
                        date: vals.date,
                        fuel_type: vals.fuel_type,
                        liters: parseFloat(vals.liters),
                        total_cost_mxn: parseFloat(vals.total_cost_mxn),
                        payment_method: vals.payment_method,
                        voucher_number: vals.voucher_number || null,
                        operator_id: vals.operator_id || null,
                        job_id: vals.job_id || null,
                        notes: vals.notes || null,
                        odometer_in_km: vals.odometer_in_km ? parseFloat(vals.odometer_in_km) : null,
                        odometer_out_km: vals.odometer_out_km ? parseFloat(vals.odometer_out_km) : null,
                        hour_meter_in: vals.hour_meter_in ? parseFloat(vals.hour_meter_in) : null,
                        hour_meter_out: vals.hour_meter_out ? parseFloat(vals.hour_meter_out) : null,
                    };
                    await api.patch(`/fuel-logs/${editing.id}`, body);
                    onSaved(dict.fuel.updateOk);
                } else {
                    const fd = new FormData();
                    fd.append("asset_id", vals.asset_id);
                    fd.append("date", vals.date);
                    fd.append("fuel_type", vals.fuel_type);
                    fd.append("liters", vals.liters);
                    fd.append("total_cost_mxn", vals.total_cost_mxn);
                    fd.append("payment_method", vals.payment_method);
                    if (vals.voucher_number) fd.append("voucher_number", vals.voucher_number);
                    if (vals.operator_id) fd.append("operator_id", vals.operator_id);
                    if (vals.job_id) fd.append("job_id", vals.job_id);
                    if (vals.notes) fd.append("notes", vals.notes);
                    if (vals.odometer_in_km) fd.append("odometer_in_km", vals.odometer_in_km);
                    if (vals.odometer_out_km) fd.append("odometer_out_km", vals.odometer_out_km);
                    if (vals.hour_meter_in) fd.append("hour_meter_in", vals.hour_meter_in);
                    if (vals.hour_meter_out) fd.append("hour_meter_out", vals.hour_meter_out);
                    if (file) fd.append("file", file);
                    await api.post("/fuel-logs", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    onSaved(dict.fuel.saveOk);
                }
            } catch (e2) {
                setErr(formatApiError(e2));
            } finally { setBusy(false); }
        });
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto p-4" data-testid="fuel-modal">
            <form onSubmit={submit} noValidate className="w-full max-w-2xl bg-slate-900 rounded-2xl border border-slate-700 p-5 space-y-4 my-4">
                <div className="flex items-baseline justify-between">
                    <h2 className="font-display text-lg font-bold">{editing ? dict.fuel.form.editTitle : dict.fuel.form.title}</h2>
                    <button type="button" onClick={onClose} data-testid="fuel-modal-close" className="text-slate-400 hover:text-slate-200"><X size={18}/></button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label={dict.fuel.form.asset} required error={form.errors.asset_id}>
                        <SelectInput {...form.field("asset_id")} data-testid="fuel-form-asset"
                            options={[{ value: "", label: dict.fuel.form.pickAsset }, ..._assets.map(a => ({ value: a.id, label: `${a.nickname} · ${a.subtype}` }))]}
                            disabled={!!editing}/>
                    </Field>
                    <Field label={dict.fuel.form.date} required error={form.errors.date}>
                        <DateInput {...form.field("date")} data-testid="fuel-form-date"/>
                    </Field>
                </div>

                <Field label={dict.fuel.form.fuelType} required>
                    <div className="flex gap-2 flex-wrap" data-testid="fuel-form-type-toggle">
                        {FUEL_TYPES.map(t => (
                            <button key={t} type="button" onClick={() => form.set("fuel_type", t)} data-testid={`fuel-form-type-${t}`}
                                className={`px-3 h-9 rounded-full border text-xs ${v.fuel_type===t?"bg-brand-orange text-slate-950 border-brand-orange font-semibold":"bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                {dict.fuel.types[t]}
                            </button>
                        ))}
                    </div>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label={dict.fuel.form.liters} required error={form.errors.liters}>
                        <NumberInput {...form.field("liters")} data-testid="fuel-form-liters" step="0.01" placeholder="0"/>
                    </Field>
                    <Field label={dict.fuel.form.totalCost} required error={form.errors.total_cost_mxn}>
                        <NumberInput {...form.field("total_cost_mxn")} data-testid="fuel-form-cost" step="0.01" placeholder="0"/>
                    </Field>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.fuel.form.livePriceHint.replace("{price}", "")}</div>
                        <div className="h-10 flex items-center justify-center font-mono text-brand-orange text-lg font-bold" data-testid="fuel-form-price-per-liter">
                            {price != null ? fmtMXN(price) : "—"}
                        </div>
                    </div>
                </div>

                <Field label={dict.fuel.form.paymentMethod} required>
                    <div className="flex gap-2 flex-wrap" data-testid="fuel-form-method-toggle">
                        {PAYMENT_METHODS.map(m => (
                            <button key={m} type="button" onClick={() => form.set("payment_method", m)} data-testid={`fuel-form-method-${m}`}
                                className={`px-3 h-9 rounded-full border text-xs ${v.payment_method===m?"bg-brand-orange text-slate-950 border-brand-orange font-semibold":"bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                {dict.fuel.methods[m]}
                            </button>
                        ))}
                    </div>
                </Field>

                {(v.payment_method === "vale" || v.payment_method === "tarjeta_combustible") && (
                    <Field label={dict.fuel.form.voucher}>
                        <TextInput {...form.field("voucher_number")} data-testid="fuel-form-voucher" placeholder="V-1234"/>
                    </Field>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label={dict.fuel.form.operator}>
                        <SelectInput {...form.field("operator_id")} data-testid="fuel-form-operator"
                            options={[{ value: "", label: dict.fuel.form.pickOperator }, ..._operators.map(o => ({ value: o.id, label: o.full_name }))]}/>
                    </Field>
                    <Field label={dict.fuel.form.job}>
                        <SelectInput {...form.field("job_id")} data-testid="fuel-form-job"
                            options={[{ value: "", label: dict.fuel.form.noJob }, ...availableJobs.map(j => ({ value: j.id, label: `${j.code} · ${j.description?.slice(0,30) || ""}` }))]}
                            disabled={!v.asset_id}/>
                    </Field>
                </div>

                <details className="border border-slate-800 rounded-lg p-3">
                    <summary className="text-xs uppercase tracking-wider text-slate-400 cursor-pointer">Odómetros y foto (opcional)</summary>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <Field label={dict.fuel.form.odomIn}><NumberInput {...form.field("odometer_in_km")} data-testid="fuel-form-odom-in"/></Field>
                        <Field label={dict.fuel.form.odomOut}><NumberInput {...form.field("odometer_out_km")} data-testid="fuel-form-odom-out"/></Field>
                        <Field label={dict.fuel.form.hourIn}><NumberInput {...form.field("hour_meter_in")} data-testid="fuel-form-hour-in"/></Field>
                        <Field label={dict.fuel.form.hourOut}><NumberInput {...form.field("hour_meter_out")} data-testid="fuel-form-hour-out"/></Field>
                    </div>
                    {!editing && (
                        <div className="mt-3">
                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.fuel.form.receipt}</label>
                            <input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="fuel-form-file" className="text-xs text-slate-300"/>
                        </div>
                    )}
                </details>

                <Field label={dict.fuel.form.notes}>
                    <Textarea {...form.field("notes")} data-testid="fuel-form-notes" rows={2}/>
                </Field>

                {err && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="fuel-form-error">{err}</div>}

                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-700 bg-slate-950 text-slate-300 text-sm" data-testid="fuel-modal-cancel">
                        {dict.fuel.form.cancel}
                    </button>
                    <button type="submit" disabled={busy} className="h-10 px-4 rounded-lg bg-brand-orange text-slate-950 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="fuel-modal-submit">
                        {busy && <Loader2 className="animate-spin" size={14}/>}
                        {dict.fuel.form.save}
                    </button>
                </div>
            </form>
        </div>
    );
}
